/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  TypeRampV2,
  Button,
  sizes,
  MenuSeparator,
  Menu,
  MenuItem,
  useMenu,
  useMenuOpenState,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
} from '@smartsheet/lodestar-core';
import { ToolbarChevronUpIcon, SettingsIcon } from '@smartsheet/lodestar-icons';
import { utcToZonedTime, format } from 'date-fns-tz';
import React, { useState } from 'react';
import useCollapse from 'react-collapsed';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch, batch } from 'react-redux';
import { Schedule } from '../../../../../../services/api/SchedulesService';
import {
  selectRunOptions,
  selectUnitOptions,
  setIsUpsertScheduleVisible,
  setSelectedScheduleIndex,
  deleteScheduleData,
} from '../../../../../../services/ducks/schedulesDuck';
import { findSavedOptionFromValue } from '../../../../../../utils/selectMenu';

const titleContainerStyle = css`
  align-items: baseline;
`;

const titlePaddingSize = sizes.xxSmall;

const titleStyle = css`
  padding-left: ${titlePaddingSize}px;
`;
const leftSpacing = sizes.large + titlePaddingSize;
const dataContainer = css`
  padding-top: ${sizes.small}px;
  padding-left: ${leftSpacing}px;
`;

const dataEntryColumnLeft = css`
  width: 100px;
`;

const separatorStyle = css`
  && {
    margin-top: ${sizes.medium}px;
    margin-left: ${leftSpacing}px;
  }
`;

interface DataEntryProps {
  textLeft: string;
  textRight: string;
  enableMargin?: boolean;
}

const DataEntry = (props: DataEntryProps) => {
  const { textLeft, textRight, enableMargin = true } = props;
  const dataEntryContainer = css`
    display: flex;
    ${enableMargin ? `margin-bottom: ${sizes.xSmall}px;` : ''}
  `;
  return (
    <div css={dataEntryContainer}>
      <TypeRampV2
        css={dataEntryColumnLeft}
        system="lodestar"
        variant="bodyStrong"
        renderAs="div"
      >
        {textLeft}:
      </TypeRampV2>
      <TypeRampV2 system="lodestar" variant="bodyText" renderAs="div">
        {textRight}
      </TypeRampV2>
    </div>
  );
};

interface ListItemProps {
  scheduleIndex: number;
  schedule: Schedule;
  isLastItem?: boolean;
}

export const ListItem = (props: ListItemProps) => {
  const { scheduleIndex, schedule, isLastItem } = props;
  const { t: translate } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const { getToggleProps, getCollapseProps } = useCollapse({
    isExpanded,
  });
  const chevronStyle = css`
    transition: transform 200ms;
    ${isExpanded ? '' : 'transform: rotate(90deg)'}
  `;

  const getFormattedTimeInUTC = (date: string) => {
    const timezone = 'UTC';
    const timeFormat = `HH:mm, dd MMM yyyy '${timezone}'`;
    return format(utcToZonedTime(new Date(date), timezone), timeFormat);
  };

  const startAtDateString = getFormattedTimeInUTC(schedule.startAt);
  const nextRunDateString = getFormattedTimeInUTC(schedule.runAt);

  const possibleUnitLabels = useSelector(selectUnitOptions);
  const getCustomLabel = () => {
    const [, amount, unit] = schedule.frequency.split(' ');
    return `${translate('designer:triggerPanel:schedules:every')} ${amount} ${
      findSavedOptionFromValue(possibleUnitLabels, unit)?.label
    }`;
  };

  const selectedFrequency = findSavedOptionFromValue(
    useSelector(selectRunOptions),
    schedule.frequency
  );

  const openHelpers = useMenuOpenState();
  const { getTargetHTMLProps, getMenuProps } = useMenu({
    ...openHelpers,
  });
  const dispatch = useDispatch();

  const enableEditMode = () => {
    batch(() => {
      dispatch(setSelectedScheduleIndex(scheduleIndex));
      dispatch(setIsUpsertScheduleVisible(true));
    });
  };

  const deleteSchedule = () => {
    dispatch(deleteScheduleData(schedule.id));
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const closeModal = () => {
    setIsDeleteModalOpen(false);
  };
  return (
    <div>
      <div css={titleContainerStyle}>
        <Button
          size={'medium'}
          iconBefore={
            <ToolbarChevronUpIcon size="xxSmall" css={chevronStyle} />
          }
          appearance="borderless"
          {...getToggleProps({
            onClick: () => {
              setIsExpanded(!isExpanded);
            },
          })}
        >
          <TypeRampV2
            css={titleStyle}
            system="lodestar"
            variant="titlesEmphasis"
          >
            {`${translate('designer:triggerPanel:schedules:schedule')} ${
              scheduleIndex + 1
            }`}
          </TypeRampV2>
        </Button>
        <Button
          size={'medium'}
          iconBefore={<SettingsIcon size="xxSmall" />}
          appearance="borderless"
          {...getTargetHTMLProps()}
        />
      </div>
      <Menu
        {...getMenuProps({
          'aria-label': 'Import Menu',
          includeBlanket: true,
        })}
      >
        <MenuItem onClick={enableEditMode}>
          <TypeRampV2 system="lodestar" variant="bodyText">
            {translate('designer:triggerPanel:schedules:list:edit')}
          </TypeRampV2>
        </MenuItem>
        <MenuItem onClick={() => setIsDeleteModalOpen(true)}>
          <TypeRampV2 system="lodestar" variant="bodyText">
            {translate('designer:triggerPanel:schedules:list:delete')}
          </TypeRampV2>
        </MenuItem>
      </Menu>
      <Modal
        width="small"
        isOpen={isDeleteModalOpen}
        onCloseRequested={closeModal}
      >
        <ModalHeader
          onCloseRequested={closeModal}
          title={translate('designer:triggerPanel:schedules:deleteModal:title')}
        />
        <ModalBody>
          {translate('designer:triggerPanel:schedules:deleteModal:description')}
        </ModalBody>
        <ModalFooter>
          <ModalFooterButton appearance="secondary" onClick={closeModal}>
            {translate('common:cancel')}
          </ModalFooterButton>
          <ModalFooterButton appearance="destructive" onClick={deleteSchedule}>
            {translate('designer:triggerPanel:schedules:list:delete')}
          </ModalFooterButton>
        </ModalFooter>
      </Modal>
      <div css={dataContainer} {...getCollapseProps()}>
        <DataEntry
          textLeft={translate('designer:triggerPanel:schedules:list:startTime')}
          textRight={startAtDateString}
        />
        <DataEntry
          textLeft={translate('designer:triggerPanel:schedules:list:frequency')}
          textRight={selectedFrequency?.label || getCustomLabel()}
        />
        <DataEntry
          textLeft={translate('designer:triggerPanel:schedules:list:nextRun')}
          textRight={nextRunDateString}
          enableMargin={false}
        />
      </div>
      {isLastItem ? <MenuSeparator css={separatorStyle} /> : null}
    </div>
  );
};
